"use client";

import { type FC, useEffect, useState } from "react";
import { motion } from "motion/react";
import { AppImage } from "@components/NextImage";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/SignInIntro/IntroFreeCheckupChip";
import { SIGN_IN_INTRO_ASSETS } from "./introSlidesConfig";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

const I18n = I18nUtil.createScoped(Translations);

export interface IntroFreeCheckupChipProps {
  className?: string;
  /** PC：箭头在左侧；App：箭头在下方 */
  layout?: "app" | "pc";
  label?: string;
}

/**
 * Free money check-up chip — Figma 102803:53566 / PC 102839:40351
 * 动效：入场延迟 600ms、translateY 12→0（450ms）；循环 0→−6→0（700ms）
 *
 * gift 已入 icon-svg-gen/assets/iconfonts/gift.svg；在 buildIconFont 落地前用 AppImage
 * （audit #6 / AGENTS Icon 规则：SVG 已入库，待字体生成后改为 `<Icon name="gift" />`）。
 */
export const IntroFreeCheckupChip: FC<IntroFreeCheckupChipProps> = ({
  className,
  layout = "app",
  label,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const [entered, setEntered] = useState(reducedMotion);

  useEffect(() => {
    if (reducedMotion) {
      setEntered(true);
      return;
    }
    const timer = window.setTimeout(() => setEntered(true), 600);
    return () => window.clearTimeout(timer);
  }, [reducedMotion]);

  const chip = (
    <motion.div
      className={cn(
        "flex h-6 items-center justify-center gap-1 overflow-hidden",
        "rounded-md bg-content_aura_lv3_primary pl-2 pr-3 py-1",
        className,
      )}
      data-testid="intro-free-checkup-chip"
      data-node-id="102803:53566"
      initial={reducedMotion ? false : { y: 12, opacity: 0 }}
      animate={
        entered
          ? reducedMotion
            ? { y: 0, opacity: 1 }
            : {
                y: [0, -6, 0],
                opacity: 1,
              }
          : { y: 12, opacity: 0 }
      }
      transition={
        entered && !reducedMotion
          ? {
              y: {
                duration: 0.7,
                repeat: Infinity,
                ease: "easeInOut",
              },
              opacity: { duration: 0.45, ease: "easeOut" },
            }
          : { duration: 0.45, ease: "easeOut" }
      }
    >
      <AppImage
        src={SIGN_IN_INTRO_ASSETS.giftIcon}
        alt=""
        width={16}
        height={16}
        className="size-4 shrink-0"
        data-node-id="102803:53567"
      />
      <span className="body_bold_s whitespace-nowrap text-content_primary">
        {I18n.t("Free")}
      </span>
      <span className="body_medium_s whitespace-nowrap text-content_primary">
        {label ?? I18n.t("money check-up")}
      </span>
    </motion.div>
  );

  if (layout === "pc") {
    return (
      <div
        className="flex items-center"
        data-testid="intro-free-checkup-chip-wrap"
        data-node-id="102839:40349"
      >
        <span
          className="mr-0 size-0 border-y-[5px] border-r-[6px] border-y-transparent border-r-content_aura_lv3_primary"
          aria-hidden
        />
        {chip}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center"
      data-testid="intro-free-checkup-chip-wrap"
      data-node-id="102803:53565"
    >
      {chip}
      <span
        className="size-0 border-x-[5.5px] border-t-[6px] border-x-transparent border-t-content_aura_lv3_primary"
        aria-hidden
      />
    </div>
  );
};
